import { computed, ref, watch, watchEffect, type UnwrapNestedRefs } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import { useChannelCapabilities } from "@/composables/use-channel-capabilities";
import { useAuthStore } from "@/stores/auth/auth.store";
import type { WebThreeTokenWithBalance } from "@/constants/web-three";
import { useMutation, useQuery } from "@urql/vue";
import { graphql as gql } from "@/generated";
import { AppKitConnector } from "@/services/wallet/app-kit-connector";
import {
  useWebThreeTokenListBalance,
  type TokenToWatchBalance,
} from "@/composables/web-three/use-web-three-token-list-balance";
import { useSimpleObservableSubject } from "@/composables/use-simple-observable-subject";
import { waitFor } from "@/utils/misc";
import { useCapabilitiesStore } from "@/stores/capabilities.store";

const MAX_TOKEN_TRANSACTION_SYNC_RETRIES = 10;

const MyWalletsDocument = gql(/* GraphQL */ `
  query MyWallets {
    myWallets {
      ... on UserWebThreeWalletList {
        wallets {
          address
          chain_id
          user_web_three_wallet_token_balances {
            web_three_erc_twenty_token_address
            balance
          }
        }
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);

const SyncWalletTokensDocument = gql(/* GraphQL */ `
  mutation SyncWalletTokens(
    $tokenAddresses: [String!]
    $syncAllChannelTokens: Boolean! = false
  ) {
    syncWalletTokens(
      token_addresses: $tokenAddresses
      sync_all_channel_tokens: $syncAllChannelTokens
    ) {
      ... on SyncWalletTokensSuccess {
        success
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);

const GetChannelWebThreeTokensDocument = gql(/* GraphQL */ `
  query GetChannelWebThreeTokens($channelId: String!) {
    channelWebThreeErcTwentyTokens(channel_id: $channelId) {
      address
      label
      symbol
      decimals
      external_partner_id
      external_partner
    }
  }
`);

export interface WalletSessionInfo {
  walletAddress: string;
}

/**
 * Service for wallet operations.
 *
 * Handles:
 * - Wallet connection/disconnection via AppKit
 * - Token balance tracking
 * - Wallet sync with backend
 *
 * This is a capability service - it provides data that gating strategies consume.
 *
 * @internal Use `useWalletService()` or `useMaybeWalletService()` to access this service.
 */
export function setupWalletCapability() {
  const channelStore = useChannelStore();
  const authStore = useAuthStore();
  const { isWalletEnabled } = useChannelCapabilities();

  // AppKit instance
  const appKit = ref<AppKitConnector | null>(null);
  const initialized = ref(false);

  // Wallet session state
  const walletSessionToken = ref<string | null>(null);
  const walletSessionInfo = ref<WalletSessionInfo | null>(null);
  const wasConnectedInSession = ref(false);
  const isConnecting = ref(false);

  // Computed state — derived from DB-backed myWallets query
  const isConnected = computed(
    () => authStore.hasAuthToken && webThreeWalletBalances.value.length > 0,
  );
  const walletAddress = computed(
    () =>
      walletSessionInfo.value?.walletAddress ??
      webThreeWalletBalances.value[0]?.address ??
      null,
  );

  // Initialize AppKit when wallet capability is enabled
  watchEffect(async (onCleanup) => {
    if (!isWalletEnabled.value) return;

    const connector = new AppKitConnector();
    await connector.init();
    appKit.value = connector;

    const unsubscribeToken = connector.onWebThreeToken.subscribe((token) => {
      walletSessionToken.value = token ?? null;
    });

    const unsubscribeConnected = connector.onWalletConnectedInSession.subscribe(
      (connected) => {
        wasConnectedInSession.value = connected;
      },
    );

    connector.onAccount.subscribe((account) => {
      if (!account?.isConnected || !account.address) return;
      walletSessionInfo.value = {
        walletAddress: account.address,
      };
    });

    initialized.value = true;

    onCleanup(() => {
      connector.dispose();
      unsubscribeToken();
      unsubscribeConnected();
      initialized.value = false;
      appKit.value = null;
    });
  });

  // Disconnect wallet when user logs out
  watch(
    () => authStore.hasAuthToken,
    async (hasToken) => {
      if (!hasToken && appKit.value) {
        await appKit.value.logout();
      }
    },
    { immediate: true },
  );

  // GraphQL queries and mutations
  const {
    data: webThreeWalletBalancesResult,
    executeQuery: refetchWalletBalance,
  } = useQuery({
    query: MyWalletsDocument,
    variables: {},
    pause: computed(() => !authStore.hasAuthToken),
  });

  // Refetch wallet data on login so a previously paired wallet is recognized immediately
  watch(
    () => authStore.hasAuthToken,
    (hasToken, prevHasToken) => {
      if (hasToken && !prevHasToken) {
        refetchWalletBalance();
      }
    },
  );

  const { data: channelWebThreeTokensResult } = useQuery({
    query: GetChannelWebThreeTokensDocument,
    variables: {
      channelId: channelStore.channelSlug,
    },
    pause: computed(() => !channelStore.channelSlug || !isWalletEnabled.value),
  });

  const { executeMutation: syncWalletTokens } = useMutation(
    SyncWalletTokensDocument,
  );

  // Channel tokens
  const channelWebThreeTokens = computed(() => {
    return (
      channelWebThreeTokensResult.value?.channelWebThreeErcTwentyTokens ?? []
    );
  });

  // Wallet balances — myWallets returns a union; unwrap UserWebThreeWalletList on success
  const webThreeWalletBalances = computed(() => {
    const result = webThreeWalletBalancesResult.value?.myWallets;
    if (!result || "error" in result) return [];
    return result.wallets ?? [];
  });

  // Token balance map - aggregates balances across all connected wallets
  const tokenBalanceMap = computed(() => {
    const map = new Map<string, WebThreeTokenWithBalance>();

    // Initialize with channel tokens (0 balance)
    for (const token of channelWebThreeTokens.value) {
      map.set(token.address, {
        symbol: token.symbol,
        label: token.label,
        address: token.address,
        external_partner_id: token.external_partner_id ?? null,
        amount: 0,
      });
    }

    // Add balances from connected wallets
    for (const wallet of webThreeWalletBalances.value) {
      for (const token of wallet.user_web_three_wallet_token_balances) {
        const webThreeErcTwentyToken = channelWebThreeTokens.value.find(
          (t) => t.address === token.web_three_erc_twenty_token_address,
        );

        if (!webThreeErcTwentyToken) continue;

        const key = token.web_three_erc_twenty_token_address;
        const previousBalance = map.get(key) ?? {
          ...webThreeErcTwentyToken,
          external_partner_id:
            webThreeErcTwentyToken.external_partner_id ?? null,
          amount: 0,
        };

        map.set(key, {
          ...previousBalance,
          amount: previousBalance.amount + token.balance,
        });
      }
    }

    return map;
  });

  // Token update observable for real-time balance updates
  const { watchSubject: watchTokenUpdates, notifySubject: notifyTokenUpdates } =
    useSimpleObservableSubject<TokenToWatchBalance>();

  // Watch for token balance changes via blockchain
  useWebThreeTokenListBalance({
    tokenList: () => {
      const list: TokenToWatchBalance[] = [];

      for (const wallet of webThreeWalletBalances.value) {
        for (const token of wallet.user_web_three_wallet_token_balances) {
          const tokenAddress = token.web_three_erc_twenty_token_address;
          const tokenSymbol = tokenBalanceMap.value.get(tokenAddress)?.symbol;

          if (!tokenSymbol) continue;

          list.push({
            tokenSymbol,
            tokenAddress,
            walletAddress: wallet.address,
            chainId: wallet.chain_id,
          });
        }
      }

      return list;
    },
    async listener(token) {
      console.log("[WalletService] Token upgrade detected", { token });

      if (!isConnected.value) {
        console.warn("[WalletService] Wallet not connected, skipping sync.");
        return;
      }

      const existingTokenBalance =
        tokenBalanceMap.value.get(token.tokenAddress)?.amount ?? 0;

      const success = await syncAndRefreshTokenBalanceAfterTransaction({
        tokenAddress: token.tokenAddress,
        existingTokenBalance,
      });

      if (success) {
        notifyTokenUpdates(token);
      }
    },
  });

  // Sync wallet when connected
  watchEffect(async () => {
    if (!isConnected.value || !authStore.hasAuthToken) return;

    await syncAndRefetchWallet({
      syncAllChannelTokens: true,
      tokenAddresses: null,
    });
  });

  // Wallet actions
  async function connect(): Promise<boolean> {
    if (!appKit.value) return false;

    try {
      isConnecting.value = true;
      await appKit.value.triggerActiveSessionOrAuth();
      return true;
    } catch (error) {
      console.error("[WalletService] Connection failed:", error);
      return false;
    } finally {
      isConnecting.value = false;
    }
  }

  async function disconnect(): Promise<void> {
    if (!appKit.value) return;
    await appKit.value.logout();
  }

  async function syncAndRefetchWallet({
    tokenAddresses,
    syncAllChannelTokens,
  }: {
    syncAllChannelTokens: boolean;
    tokenAddresses: string[] | null;
  }) {
    await syncWalletTokens({
      tokenAddresses,
      syncAllChannelTokens,
    });
    await refetchWalletBalance();
  }

  // Sync wallet state when session token changes (connect/disconnect via AppKit).
  // When a token appears (wallet connected), sync balances from chain to DB
  // and refetch the MyWallets query so isConnected/walletAddress update reactively.
  // When token is cleared (disconnect), refetch to clear balances.
  watch(walletSessionToken, async (token, prevToken) => {
    if (token && !prevToken && authStore.hasAuthToken) {
      await syncAndRefetchWallet({
        syncAllChannelTokens: true,
        tokenAddresses: null,
      });
    } else if (!token && prevToken) {
      await refetchWalletBalance();
    }
  });

  async function syncAndRefreshTokenBalanceAfterTransaction({
    tokenAddress,
    existingTokenBalance,
    retryCount = 0,
  }: {
    tokenAddress: string;
    existingTokenBalance: number;
    retryCount?: number;
  }): Promise<boolean> {
    await syncAndRefetchWallet({
      syncAllChannelTokens: true,
      tokenAddresses: null,
    });

    const newBalance = tokenBalanceMap.value.get(tokenAddress)?.amount ?? 0;

    // If balance changed, transaction was processed
    if (newBalance !== existingTokenBalance) return true;

    // Retry if balance hasn't changed yet
    if (retryCount < MAX_TOKEN_TRANSACTION_SYNC_RETRIES) {
      await waitFor(1000);
      return syncAndRefreshTokenBalanceAfterTransaction({
        tokenAddress,
        existingTokenBalance,
        retryCount: retryCount + 1,
      });
    }

    console.warn(
      "[WalletService] Failed to refresh token balance after transaction",
      { tokenAddress, existingTokenBalance, retryCount },
    );

    return false;
  }

  async function syncAllTokenBalances(): Promise<void> {
    if (!isConnected.value) return;

    await syncAndRefetchWallet({
      syncAllChannelTokens: true,
      tokenAddresses: null,
    });
  }

  function getTokenBalance(tokenAddress: string): number {
    return tokenBalanceMap.value.get(tokenAddress)?.amount ?? 0;
  }

  // Dispose function for cleanup
  function dispose() {
    if (appKit.value) {
      appKit.value.dispose();
      appKit.value = null;
    }
    initialized.value = false;
    walletSessionToken.value = null;
    walletSessionInfo.value = null;
    wasConnectedInSession.value = false;
  }

  // Service readiness - indicates AppKit is initialized
  const isReady = computed(
    () => initialized.value && channelWebThreeTokensResult.value,
  );

  return {
    // State
    initialized,
    isConnected,
    isConnecting,
    walletAddress,
    walletSessionInfo,
    wasConnectedInSession,
    tokenBalanceMap,
    channelWebThreeTokens,

    // Actions
    connect,
    disconnect,
    syncAllTokenBalances,
    getTokenBalance,
    refetchWalletBalance,

    // Token update observable
    watchTokenUpdates,

    // Lifecycle
    isReady,
    dispose,
  };
}

// Type alias for the unwrapped wallet service (refs are auto-unwrapped by Pinia)
export type WalletService = UnwrapNestedRefs<
  ReturnType<typeof setupWalletCapability>
>;

/**
 * Access the wallet service. Throws if the service is not initialized.
 * Use this in components that are rendered within the WalletCapabilityManager hierarchy.
 *
 * Note: All reactive refs are auto-unwrapped by Pinia, so access properties
 * directly (e.g., `walletService.isConnected` not `walletService.isConnected.value`).
 */
export function useWalletService(): WalletService {
  const store = useCapabilitiesStore();
  if (!store.walletService) {
    throw new Error("WalletService not initialized");
  }
  return store.walletService;
}

/**
 * Access the wallet service if available, or null if not initialized.
 * Use this in general app components that may render outside the capability manager hierarchy.
 *
 * Note: All reactive refs are auto-unwrapped by Pinia, so access properties
 * directly (e.g., `walletService.isConnected` not `walletService.isConnected.value`).
 */
export function useMaybeWalletService(): { value: WalletService | null } {
  const store = useCapabilitiesStore();

  return {
    get value() {
      return store.walletService;
    },
  };
}
